import { Badge, Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { DataTable, PageHeader } from "@how/ui";
import { CrmTabs } from "./CrmTabs";
import { TagModal } from "./TagModal";
import { useTags, type TagRow } from "./tag-queries";

const columns: ColumnDef<TagRow>[] = [
  {
    accessorKey: "name",
    header: "Tag",
    enableSorting: false,
    cell: (ctx) => (
      <Badge variant="light" color={semanticColor(ctx.row.original.color)}>
        {ctx.row.original.name}
      </Badge>
    ),
  },
  {
    accessorKey: "description",
    header: "What it means",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.description === null ? (
        <Text size="sm" c="dimmed">
          No description
        </Text>
      ) : (
        <Text size="sm">{ctx.row.original.description}</Text>
      ),
  },
  { accessorKey: "peopleCount", header: "People", enableSorting: false, meta: { numeric: true } },
];

const PAGE = { pageIndex: 0, pageSize: 200 };

/**
 * The church's tags. Small by nature, so read whole rather than paged; the reviewed list pattern is otherwise
 * unchanged, and a row opens the same dialog that creates a tag.
 */
export function TagsPage() {
  const tags = useTags();
  const canManage = usePermission("tags.manage", { write: true });
  // undefined: closed. null: creating. A row: editing that tag.
  const [open, setOpen] = useState<TagRow | null | undefined>(undefined);
  const status = tags.isPending ? "loading" : tags.isError ? "error" : "ready";

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone your church knows, whether or not they have a login."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setOpen(null);
              }}
            >
              Add tag
            </Button>
          ) : undefined
        }
      />
      <CrmTabs active="tags" />
      <DataTable
        label="Tags"
        columns={columns}
        rows={tags.data ?? []}
        totalRows={tags.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setOpen(row);
        }}
        emptyTitle="No tags yet"
        emptyDescription="A tag is a label your church defines, such as Newcomer or Volunteer."
        errorMessage="Could not load tags."
        onRetry={() => {
          void tags.refetch();
        }}
      />
      <TagModal
        key={open === undefined ? "closed" : open === null ? "new" : open.id}
        tag={open}
        canManage={canManage.data === true}
        onClose={() => {
          setOpen(undefined);
        }}
      />
    </>
  );
}
